// Pure calculation + export helpers for the Reports screen. No fetching here --
// every report reads from the same site-scoped arrays already held in the store
// (state.recentTransactions, state.materialRecords, state.labourRecords), which
// are exactly the arrays transactions.js/materials.js/labour.js load per the
// currently selected site. This file adds no new Supabase query and no new
// financial data source -- it only filters/groups what's already loaded.

import { computeTotals } from '../pnl/pnl.js';

// Generic date-range filter reusable across transactions (entry_date),
// material_purchases (purchase_date) and labour_payments (payment_date).
export function filterByDateRange(items, dateField, from, to) {
  return items.filter((item) => {
    const d = item[dateField];
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
}

// Groups transactions by entry_date's 'YYYY-MM' into per-month P&L rows,
// sorted chronologically. Reuses computeTotals (from pnl.js) per month bucket
// so the math is identical to the rest of the app, not reimplemented.
export function monthlyBreakdown(transactions) {
  const byMonth = {};
  for (const t of transactions) {
    const month = (t.entry_date || '').slice(0, 7);
    if (!month) continue;
    if (!byMonth[month]) byMonth[month] = [];
    byMonth[month].push(t);
  }
  return Object.keys(byMonth)
    .sort()
    .map((month) => ({ month, ...computeTotals(byMonth[month]) }));
}

export function monthLabel(monthStr) {
  const [y, m] = monthStr.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

// Same per-month rows as monthlyBreakdown(), plus a running (cumulative) total
// for income and expense -- what the Overview tab's trend chart plots. Pure
// derivation from monthlyBreakdown(), no new data source.
export function cumulativeMonthlyBreakdown(transactions) {
  let cumIncome = 0;
  let cumExpense = 0;
  return monthlyBreakdown(transactions).map((r) => {
    cumIncome += r.income;
    cumExpense += r.expense;
    return { month: r.month, income: r.income, expense: r.expense, cumIncome, cumExpense };
  });
}

// ---------------------------------------------------------------------------
// CSV export -- client-side only, no backend export service.
// ---------------------------------------------------------------------------
function csvEscape(v) {
  const s = String(v ?? '');
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export function toCSV(headers, rows) {
  const lines = [headers.map(csvEscape).join(',')];
  for (const row of rows) lines.push(row.map(csvEscape).join(','));
  return lines.join('\r\n');
}

export function downloadCSV(filename, csvString) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
